/**
 * List/grid cell display — same semantics as EntityForm / RuntimeFormRenderer.
 */
import { resolveFieldValue } from '@/runtime/resolveFieldValue'
import { resolveNumberDigits, formatNumberDisplay } from '@/runtime/number/formatNumber'

export interface ListCellAttrHint {
  name: string
  attr_type?: string
  props?: Record<string, unknown>
  /** True for a translatable string attribute (Task 1) — the base column
   * resolves the active-locale sibling instead of the raw stored value. */
  translatable?: boolean
  /** Active i18next locale, carried on the hint so callers that only forward
   * `hintFor(col)` (not a separate `locale` argument) still resolve correctly. */
  locale?: string
}

/** Alignment predicate. Exact stored type only — do not widen to int/float/decimal. */
export function isNumericListColumn(hint: Pick<ListCellAttrHint, 'attr_type'> | undefined): boolean {
  return hint?.attr_type === 'number'
}

export function getRowField(row: Record<string, unknown>, fieldName: string): unknown {
  if (!fieldName) return undefined
  if (Object.prototype.hasOwnProperty.call(row, fieldName)) return row[fieldName]
  const lower = fieldName.toLowerCase()
  for (const k of Object.keys(row)) {
    if (k.toLowerCase() === lower) return row[k]
  }
  return undefined
}

export function getRowFieldDisplay(row: Record<string, unknown>, fieldName: string): unknown {
  const keys = [`${fieldName}_display`, `${fieldName}_Display`]
  for (const k of keys) {
    if (row[k] != null && row[k] !== '') return row[k]
  }
  const lower = fieldName.toLowerCase()
  for (const k of Object.keys(row)) {
    if (k.toLowerCase() === `${lower}_display`) return row[k]
  }
  return undefined
}

/** Parse YYYY-MM-DD as local calendar date (avoid UTC shift in lists). */
function parseStoredDate(val: unknown): Date | null {
  if (val == null || val === '') return null
  const s = String(val).trim()
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
    return Number.isNaN(d.getTime()) ? null : d
  }
  const d = new Date(s)
  return Number.isNaN(d.getTime()) ? null : d
}

function isRefType(t: string): boolean {
  const x = t.toLowerCase()
  return x === 'ref' || x === 'enum_ref' || x === 'composite_ref' || x.includes('reference')
}

function isBooleanType(t: string): boolean {
  const x = t.toLowerCase()
  return x === 'boolean' || x.includes('bool')
}

function isNumberType(t: string): boolean {
  const x = t.toLowerCase()
  return x === 'number' || x.includes('int') || x.includes('float') || x.includes('decimal') || x.includes('numeric')
}

function isDateType(t: string): boolean {
  const x = t.toLowerCase()
  return x === 'date' || x === 'datetime' || x === 'time' || (x.includes('date') && !x.includes('update'))
}

function coercePrimitive(val: unknown): unknown {
  if (val == null) return null
  if (typeof val !== 'object') return val
  if (val instanceof Date) return val
  const o = val as Record<string, unknown>
  if (typeof o.name === 'string' && o.name) return o.name
  if (typeof o.value === 'string' || typeof o.value === 'number') return o.value
  if (o.id != null) return String(o.id)
  return val
}

export function formatListCellValue(
  attr: ListCellAttrHint,
  row: Record<string, unknown>,
  locale?: string,
): string {
  const type = (attr.attr_type || 'string').toLowerCase()
  const format = String(attr.props?.format ?? '').toLowerCase()
  const fieldName = attr.name

  const displayRaw = getRowFieldDisplay(row, fieldName)
  if (displayRaw != null && displayRaw !== '') return String(displayRaw)

  if (attr.translatable) return resolveFieldValue(row, fieldName, locale ?? 'en')

  let val = coercePrimitive(getRowField(row, fieldName))

  if (isBooleanType(type)) {
    const truthy = val === true || val === 1 || val === '1' || val === 'true'
    return truthy ? 'Yes' : 'No'
  }

  if (val == null || val === '') return '—'

  if (isRefType(type)) {
    return String(val)
  }

  if (isDateType(type)) {
    const d = parseStoredDate(val)
    if (!d) return String(val)
    const loc = locale
    if (format === 'time' || type === 'time') {
      return d.toLocaleTimeString(loc, { hour: '2-digit', minute: '2-digit' })
    }
    if (format === 'datetime' || type === 'datetime' || String(val).includes('T')) {
      return d.toLocaleString(loc, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    }
    return d.toLocaleDateString(loc, { year: 'numeric', month: '2-digit', day: '2-digit' })
  }

  if (isNumberType(type)) {
    const n = typeof val === 'number' ? val : Number(val)
    if (!Number.isNaN(n)) {
      const { precision, scale } = resolveNumberDigits(attr.props)
      return formatNumberDisplay(n, precision, scale)
    }
  }

  return String(val)
}
